import React, { useEffect, useState } from "react";
import { Users, Home, UserCheck, Church, Cake, Activity, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { useAuth, ROLE_LABELS, roleLabelsOf } from "../lib/auth";
import { UlangTahunAnak } from "../components/UlangTahunAnak";
import { NotifAbsensiCare } from "../components/NotifAbsensiCare";
import { NotifAbsenPelayan } from "../components/NotifAbsenPelayan";
import { Card } from "../components/ui/card";
import { JadwalMingguIniCard } from "../components/JadwalMingguIniCard";
import { PerubahanKhususCard } from "../components/PerubahanKhususCard";
import { KegiatanDashboardCard } from "../components/KegiatanDashboardCard";
import { KalenderMingguIni } from "../components/KalenderMingguIni";
import { StrukturWidget } from "../components/StrukturWidget";
import { PengumumanWidget } from "../components/PengumumanWidget";
import { GrafikKehadiran } from "../components/GrafikKehadiran";
import { toast } from "sonner";

export default function DashboardPage() {
  const { user, can } = useAuth();
  const [d, setD] = useState(null);
  const [ut, setUt] = useState(null);
  const [kondisi, setKondisi] = useState(null);
  const [clRing, setClRing] = useState(null);
  useEffect(() => {
    if (user.role === "ADMIN" || user.role === "PIMPINAN") api.get("/kondisi/ringkasan").then((r) => setKondisi(r.data)).catch(() => {});
    if (user.role === "CARE_LEADER") api.get("/kondisi/ringkasan").then((r) => setClRing(r.data)).catch(() => {});
  }, [user.role]);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => {
    if (!can("jemaat:read")) return;
    const n = new Date();
    const tgl = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
    api.get("/ulang-tahun/stats", { params: { tanggal: tgl } }).then((r) => setUt(r.data)).catch(() => {});
  }, [can]);

  if (!d) return <div className="text-center py-16 text-slate-400">Memuat...</div>;

  const stats = [
    { label: "Total Jemaat", value: d.total_jemaat, icon: Users, id: "stat-total-jemaat" },
    { label: "Jemaat Aktif", value: d.per_status["Aktif"], icon: UserCheck, id: "stat-aktif" },
    { label: "Total Kartu Keluarga", value: d.total_keluarga, icon: Home, id: "stat-keluarga" },
    { label: "Sudah Baptis", value: d.per_baptisan["Sudah"], icon: Church, id: "stat-baptis" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Selamat datang, <span className="font-semibold text-slate-700" data-testid="dashboard-user-nama">{user.nama}</span> · <span data-testid="dashboard-user-role">{roleLabelsOf(user)}</span></p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {stats.map((s) => (
          <Card key={s.id} className="p-4 sm:p-5 rounded-2xl border-slate-200/80" data-testid={s.id}>
            <div className="h-9 w-9 rounded-xl bg-[hsl(224,64%,33%)]/10 text-[hsl(224,64%,33%)] flex items-center justify-center mb-3"><s.icon className="h-4 w-4" /></div>
            <div className="text-2xl sm:text-3xl font-bold text-slate-900">{s.value}</div>
            <div className="text-xs sm:text-sm text-slate-500">{s.label}</div>
          </Card>
        ))}
      </div>
      {ut && (
        <Card className="p-4 sm:p-5 rounded-2xl border-slate-200/80 flex items-center justify-between gap-4" data-testid="dashboard-ulang-tahun-card">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-11 w-11 rounded-xl bg-[hsl(24,94%,40%)]/10 text-[hsl(24,94%,40%)] flex items-center justify-center shrink-0"><Cake className="h-5 w-5" /></div>
            <div className="min-w-0">
              <div className="text-xs sm:text-sm text-slate-500">🎂 Ulang Tahun Hari Ini</div>
              <div className="text-xl sm:text-2xl font-bold text-slate-900"><span data-testid="dashboard-ulang-tahun-count">{ut.hari_ini}</span> Jemaat</div>
              {ut.pernikahan_hari_ini > 0 && <div className="text-xs sm:text-sm text-rose-600 font-medium mt-0.5" data-testid="dashboard-pernikahan-count">💍 {ut.pernikahan_hari_ini} pasangan ulang tahun pernikahan</div>}
            </div>
          </div>
          <Link to="/ulang-tahun" className="text-sm font-semibold text-[hsl(224,64%,33%)] hover:underline shrink-0 min-h-[44px] inline-flex items-center" data-testid="dashboard-ulang-tahun-link">Lihat Daftar →</Link>
        </Card>
      )}
      {can("anak:ultah") && <UlangTahunAnak />}
      {can("care_leader:read") && ["ADMIN", "PIMPINAN"].some((r) => (user?.roles || [user?.role]).includes(r)) && <><NotifAbsensiCare /><NotifAbsenPelayan /></>}
      {kondisi && (
        <Card className={`p-4 sm:p-5 rounded-2xl flex items-center justify-between gap-4 ${kondisi.perlu_perhatian_khusus > 0 ? "border-red-300 bg-red-50/70" : "border-slate-200/80"}`} data-testid="dashboard-kondisi-card">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-11 w-11 rounded-xl bg-red-100 text-red-700 flex items-center justify-center shrink-0"><Activity className="h-5 w-5" /></div>
            <div className="min-w-0">
              <div className="text-xs sm:text-sm text-slate-600">🔴 Anggota Perlu Perhatian Khusus</div>
              <div className="text-xl sm:text-2xl font-bold text-slate-900"><span data-testid="dashboard-kondisi-count">{kondisi.perlu_perhatian_khusus}</span> Anggota</div>
              <div className="text-xs text-slate-500">🟡 {kondisi.perlu_perhatian} perlu perhatian · 🟢 {kondisi.baik} baik · ⚪ {kondisi.belum_dinilai} belum dinilai</div>
            </div>
          </div>
          <Link to="/kondisi-anggota?status=Perlu%20Perhatian%20Khusus" className="text-sm font-semibold text-[hsl(224,64%,33%)] hover:underline shrink-0 min-h-[44px] inline-flex items-center" data-testid="dashboard-kondisi-link">Lihat Daftar →</Link>
        </Card>
      )}
      {clRing && (
        <Card className={`p-4 sm:p-5 rounded-2xl flex flex-wrap items-center justify-between gap-4 ${clRing.perlu_tinjau > 0 ? "border-orange-300 bg-orange-50/70" : "border-slate-200/80"}`} data-testid="dashboard-cl-tinjau-card">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-11 w-11 rounded-xl bg-orange-100 text-orange-700 flex items-center justify-center shrink-0"><Clock className="h-5 w-5" /></div>
            <div className="min-w-0">
              <div className="text-xs sm:text-sm text-slate-600">Anggota Perlu Ditinjau (belum diperbarui &gt; {clRing.hari_tinjau} hari)</div>
              <div className="text-xl sm:text-2xl font-bold text-slate-900"><span data-testid="dashboard-cl-tinjau-count">{clRing.perlu_tinjau}</span> dari {clRing.total_anggota} Anggota</div>
              <div className="text-xs text-slate-500">🔴 {clRing.perlu_perhatian_khusus} · 🟡 {clRing.perlu_perhatian} · 🟢 {clRing.baik} · ⚪ {clRing.belum_dinilai} belum dinilai</div>
            </div>
          </div>
          <Link to={clRing.perlu_tinjau > 0 ? "/kondisi-anggota?tinjau=1" : "/kondisi-anggota"} className="text-sm font-semibold text-[hsl(224,64%,33%)] hover:underline shrink-0 min-h-[44px] inline-flex items-center" data-testid="dashboard-cl-tinjau-link">{clRing.perlu_tinjau > 0 ? "Tinjau Sekarang →" : "Buka Member Monitoring →"}</Link>
        </Card>
      )}
      {kondisi && <PerubahanKhususCard />}
      {can("kegiatan:read") && <KegiatanDashboardCard />}
      {can("kegiatan:read") && <KalenderMingguIni />}
      <PengumumanWidget />
      <GrafikKehadiran />
      {can("jadwal:read") && <JadwalMingguIniCard />}
      <StrukturWidget />
      <div className="grid md:grid-cols-2 gap-4">
        <Breakdown title="Status Jemaat" data={d.per_status} total={d.total_jemaat} testId="breakdown-status" />
        <Breakdown title="Status Keanggotaan" data={d.per_keanggotaan} total={d.total_jemaat} testId="breakdown-keanggotaan" />
        <Breakdown title="Komisi" data={d.per_komisi} total={d.total_jemaat} testId="breakdown-komisi" />
        <Breakdown title="Jenis Kelamin" data={d.per_jenis_kelamin} total={d.total_jemaat} testId="breakdown-jk" />
      </div>
    </div>
  );
}

const Breakdown = ({ title, data, total, testId }) => (
  <Card className="p-5 rounded-2xl border-slate-200/80" data-testid={testId}>
    <h3 className="font-semibold text-slate-800 mb-4">{title}</h3>
    <div className="space-y-3">
      {Object.entries(data).map(([k, v]) => (
        <div key={k}>
          <div className="flex justify-between text-sm mb-1"><span className="text-slate-600">{k}</span><span className="font-semibold text-slate-800">{v}</span></div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[hsl(224,64%,33%)] rounded-full transition-[width] duration-500" style={{ width: total ? `${(v / total) * 100}%` : 0 }} /></div>
        </div>
      ))}
    </div>
  </Card>
);
